import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import api from '../../utils/api'
import CaseSelect from './CaseSelect'
import useActivityMenuPlacement from './useActivityMenuPlacement'
import ActivityDurationSelect from './ActivityDurationSelect'
import { clockToMinutes, minutesToClock, normalizeClockInput, isInvalidClock } from './activityTime'
import { usLongDate } from './activityDisplay'
import {
    CHIP_OFF,
    CHIP_ON,
    EVENT_COLOR,
    T,
    consequenceOf,
    dayMessage,
    fmtMinutes,
    pickConflict,
    toMinutes,
} from './activityStepper.config'

/**
 * Add Activity — ONE PAGE (14 Oct design, "V3 - Single page").
 *
 * THE CASE-MANAGER COPY. Identical to the mediator's, with one difference that
 * runs through every request: `mediatorId`. A case manager acts FOR a mediator,
 * so the owner can never be inferred from the session the way it is in
 * scale-portal. cm-be stamps the case manager's own id from their token and
 * scale-be verifies the assignment, so a mediator id typed into a request that
 * is not assigned to this case manager is refused there — the prop selects
 * among mediators they already have, it does not grant access to one.
 *
 * It also has no toast: the mediator's portal confirms a save with one, and
 * here the caller closes the dialog and refreshes instead.
 *
 * The three steps below became three bands of a single dialog: details, date
 * and time, and "Your day". Nothing about what the form DOES changed — the
 * same fields, the same day lookup, the same conflict ladder and the same save
 * payload. The history that follows explains those rules and still applies.
 *
 * Add Activity — the three-step flow from Adam's 13 Oct mockup.
 *
 * WHY A STEPPER REPLACED THE SINGLE FORM
 * --------------------------------------
 * The old form put everything on one surface and bolted the clash list onto the
 * bottom at save time. That produced the two complaints the call kept returning
 * to: the Time field "says optional but I feel like I have to put in a time",
 * when in Adam's words "90 plus percent of these won't have a time"; and the
 * clash list arrived as a surprise after pressing Save, as a warning, for
 * something the product does not treat as a hazard.
 *
 * Three steps fix both. Time lives on its own step where NOT setting one is the
 * default and adding one is a deliberate "+ Add a time". And the day is shown
 * BEFORE saving, as the mediator's own day rather than a warning — which is
 * also the only place the open-slot decision can honestly be asked.
 *
 * WHAT IS GENUINELY NEW, not just rearranged
 * ------------------------------------------
 * Step 3 reads the WHOLE day from GET /mediator-activities/day — bookings,
 * other activities and the availability slots still open for a party to book.
 * The old 409 only ever returned clashes and never looked at open slots, so the
 * decision Adam asked for could not be asked at all.
 */

const TYPES = [
    { value: 'follow_up_call', label: 'Follow-up call', short: 'Follow-up' },
    { value: 'pre_mediation_call', label: 'Pre-mediation call', short: 'Pre-mediation' },
    { value: 'status_check_in', label: 'Status check-in', short: 'Status check-in' },
    { value: 'personal', label: 'Personal', short: 'Personal' },
    { value: 'other', label: 'Other', short: 'Activity' },
]

const METHODS = [
    { value: 'phone', label: 'Phone' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'google_meet', label: 'Google Meet' },
    { value: 'email', label: 'Email' },
]


/* The colon is TYPED FOR YOU, and it lands as soon as the hour is complete —
 * after the second digit. The field reads hh:mm throughout, which is what the
 * 00:00 placeholder promises, so 0930 becomes 09:30 as it is typed.
 * Punctuation a mediator has to remember is punctuation they get wrong, and
 * the control this replaced made them pick from a list of 96. */
/* Controls that sit INSIDE a bordered field share these, so the clock, the
 * AM/PM pair and the duration cannot drift apart from one another. */
const INPUT_IN_FIELD = {
    height: '100%',
    border: 0,
    outline: 'none',
    background: 'transparent',
    font: 'inherit',
    fontSize: 15,
    color: '#18211F',
    fontVariantNumeric: 'tabular-nums',
}
const DIVIDER_IN_FIELD = {
    width: 1,
    alignSelf: 'stretch',
    background: '#D5DCDA',
    flex: 'none',
}

const label13 = { fontSize: 13, fontWeight: 600, marginBottom: 9, color: T.inkSoft }
const optional = (
    <span style={{ fontWeight: 400, color: T.muted, marginLeft: 5 }}>(optional)</span>
)

const helpText = {
    margin: '8px 0 0',
    fontSize: 13,
    lineHeight: 1.45,
    color: T.muted,
}

const Chip = ({ on, children, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={on}
        style={{
            whiteSpace: 'nowrap',
            flex: 'none',
            padding: '9px 12px',
            borderRadius: 7,
            font: 'inherit',
            fontSize: 14,
            lineHeight: 1.3,
            cursor: disabled ? 'default' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            borderWidth: 1,
            borderStyle: 'solid',
            opacity: disabled ? 0.6 : 1,
            ...(on ? CHIP_ON : CHIP_OFF),
        }}
    >
        {children}
    </button>
)

const AddActivityForm = ({
    /* The mediator this activity belongs to. Never inferred from the session:
       a case manager acts for someone else. */
    mediatorId,
    lockedCase = null,
    initialDate = '',
    activity = null,
    headerLead = null,
    onSaved,
    onCancel,
}) => {
    const isEditing = !!activity?.id

    /* ---------------------------------------------------------- step 1 -- */
    const [selectedCase, setSelectedCase] = useState(() => {
        if (lockedCase) return lockedCase
        if (activity?.case_id) {
            const name = String(activity.display_title || '').split(' - ').slice(1).join(' - ')
            return { id: activity.case_id, case_name: name || 'Case' }
        }
        return null
    })
    const [caseQuery, setCaseQuery] = useState('')
    const [caseOptions, setCaseOptions] = useState([])
    const [caseLoading, setCaseLoading] = useState(false)

    const [activityType, setActivityType] = useState(activity?.activity_type || '')
    const [otherName, setOtherName] = useState(activity?.other_name || '')
    const [method, setMethod] = useState(activity?.method || '')
    const [note, setNote] = useState(activity?.notes || '')

    /* ---------------------------------------------------------- step 2 -- */
    /* PREFILLED ONLY WHEN THE DAY IS KNOWN. Adam, 14 Oct: the date comes
     * filled in only when Add Activity is opened from a specific day — the
     * board's "+" on a cell, which passes that date. From the toolbar button
     * or from Case Actions there is no day in mind, so defaulting to today
     * put a date in that nobody chose and that saves silently if unnoticed.
     * Editing keeps the activity's own date. */
    const [date, setDate] = useState(activity?.local_date || initialDate || '')
    const [timeOpen, setTimeOpen] = useState(() => !!(activity && !activity.is_all_day))
    /* What the mediator typed, kept beside `time` rather than replacing it:
       `time` stays the 24h "HH:MM" everything downstream already reads. */
    const seedClock = minutesToClock(
        activity?.is_all_day ? null : toMinutes(activity?.local_time)
    )
    const [clockText, setClockText] = useState(seedClock.text)
    const [period, setPeriod] = useState(seedClock.period)


    const clockMinutes = clockToMinutes(clockText, period)
    const time = clockMinutes == null ? '' : `${String(Math.floor(clockMinutes / 60)).padStart(2, '0')}:${String(clockMinutes % 60).padStart(2, '0')}`
    const [duration, setDuration] = useState(activity?.duration_minutes || 30)
    const [dateMenu, setDateMenu] = useState(false)

    /* ---------------------------------------------------------- step 3 -- */
    const [choice, setChoice] = useState(null)

    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState('')

    /* ------------------------------------------------------ case search -- */
    /* The server side is slow by construction — "contains" matching is a
     * leading-wildcard ILIKE no index can serve. So results are cached per
     * query, and the previous ones STAY on screen while the next load: the
     * list being taken away on every keystroke is most of what felt slow. */
    const searchTimer = useRef(null)
    const searchCache = useRef(new Map())
    useEffect(() => {
        if (lockedCase) return undefined
        const q = caseQuery.trim()
        if (q.length === 1) {
            setCaseLoading(false)
            return undefined
        }
        const key = `${mediatorId}|${q}`
        if (searchCache.current.has(key)) {
            setCaseOptions(searchCache.current.get(key))
            setCaseLoading(false)
            return undefined
        }
        setCaseLoading(true)
        searchTimer.current = setTimeout(async () => {
            try {
                const res = await api.get('/activities/case-options', {
                    params: { mediatorId, q },
                })
                const rows = res?.data?.data || []
                searchCache.current.set(key, rows)
                setCaseOptions(rows)
            } catch {
                /* Previous results stay: an error is not a reason to tell the
                   case manager they have no cases. */
            } finally {
                setCaseLoading(false)
            }
        }, 220)
        return () => clearTimeout(searchTimer.current)
    }, [caseQuery, lockedCase, mediatorId])

    /* --------------------------------------------------------- the day -- */
    /* PREFETCHED FROM STEP 2, and cached per date.
     *
     * Fetching on arrival at step 3 meant the mediator watched "Loading your
     * day…" every time, because the round trip starts at the exact moment the
     * panel needs it. The date is known a whole step earlier, so the request
     * goes out then and is normally back before Next is pressed.
     *
     * The cache is what makes Back and Next free: stepping back to change a
     * duration and forward again re-rendered the same day from scratch. It is
     * keyed by date and lives for the life of the dialog — short enough that
     * nothing it holds can go stale behind the mediator. */
    const dayCache = useRef(new Map())
    const [dayVersion, setDayVersion] = useState(0)

    const loadDay = useCallback(
        (forDate) => {
            if (!forDate || dayCache.current.has(forDate)) return
            api
                .get('/activities/day', {
                    params: {
                        mediatorId,
                        date: forDate,
                        excludeActivityId: activity?.id,
                    },
                })
                .then((res) => {
                    dayCache.current.set(forDate, res?.data?.data || [])
                })
                .catch(() => {
                    /* Advisory. A day that will not load must not stop the
                       save — an empty list is cached so it is not retried on
                       every render. */
                    dayCache.current.set(forDate, [])
                })
                .finally(() => setDayVersion((v) => v + 1))
        },
        [activity?.id, mediatorId]
    )

    /* One page: the day panel is on screen from the start, so it loads with
       the dialog and again whenever the date changes. */
    useEffect(() => {
        if (date) loadDay(date)
    }, [date, loadDay])

    /* `dayVersion` is in the deps so a finished fetch re-reads the cache; the
     * ref itself does not trigger a render. */
    const dayLoaded = !!date && dayCache.current.has(date)
    const events = useMemo(
        () => dayCache.current.get(date) || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [date, dayVersion]
    )

    /* Any change to when this happens invalidates a decision made about the
     * old when. */
    useEffect(() => {
        setChoice(null)
    }, [date, time, timeOpen, duration])

    /* ------------------------------------------------------- derivation -- */
    const start = timeOpen && time ? toMinutes(time) : null
    const end = start == null ? null : start + (Number(duration) || 30)
    const timeLabel = start == null ? '' : fmtMinutes(start)

    const hits = useMemo(() => {
        if (start == null) return []
        return events.filter((e) => e.start >= 0 && start < e.end && end > e.start)
    }, [events, start, end])

    const conflict = useMemo(() => pickConflict(hits), [hits])
    const { message, choices } = useMemo(
        () => dayMessage({ events, start, timeLabel, conflict, isEditing }),
        [events, start, timeLabel, conflict, isEditing]
    )

    const decisionPending = choices.length > 0 && !choices.includes(choice)
    /* Save waits for the day as well as for the decision. Until the fetch is
       back we do not know whether there IS a decision to put to the mediator,
       and letting them save first would silently skip the open-slot question
       on exactly the day it matters. */
    /* A half-typed time BLOCKS, visibly. "Add a time" was pressed and only
       "10" entered, so there is no time to save — Save used to scroll to the
       field and return without a word, which reads as the button being
       broken. Now it is muted and the footer says what is missing. */
    /* Two different states, said two different ways. A part-typed entry is
       simply unfinished — no red, because it is a time being typed. Four
       digits that cannot be a clock time (33:33) IS a mistake, and says so. */
    /* Parsed HERE, not read from `time`. `time` is set by an effect, so it
       lags clockText by one render — on the frame you type the final digit of
       10:30 the text is already four digits while `time` is still empty, and
       the field flashed red for exactly one paint. Deriving it synchronously
       means the two can never disagree. */
    const parsedClock = clockToMinutes(clockText, period)
    const timeInvalid = timeOpen && isInvalidClock(clockText, period)
    const timeIncomplete = timeOpen && parsedClock == null
    const saveBlocked = !date || timeIncomplete || !dayLoaded || decisionPending
    const typeMeta = TYPES.find((t) => t.value === activityType)
    const caseShort = selectedCase?.case_name || ''

    /* The new activity, shown in the day list alongside everything else so the
     * mediator sees where it lands rather than being told about it. */
    const rows = useMemo(() => {
        const list = events.map((e) => {
            /* An open slot is never marked "Overlaps": it is still just
               available, and the question about it is asked under the list. */
            const overlap =
                e.kind !== 'slot' &&
                hits.some((h) => h.kind === e.kind && h.id === e.id)
            return {
                key: `${e.kind}-${e.id}`,
                from: e.start < 0 ? '' : fmtMinutes(e.start),
                to: e.start < 0 ? '' : fmtMinutes(e.end),
                name: e.name,
                sub: '',
                color: EVENT_COLOR[e.kind],
                status: overlap ? 'Overlaps' : e.kind === 'slot' ? 'Available' : '',
                sort: e.start,
                overlap,
                draft: false,
            }
        })
        list.push({
            key: '__new',
            from: timeLabel,
            to: start == null ? '' : fmtMinutes(end),
            name:
                activityType === 'other'
                    ? otherName.trim() || 'Activity'
                    : typeMeta?.label || 'Activity',
            sub: caseShort,
            color: EVENT_COLOR.act,
            status: 'This activity',
            sort: start == null ? -1 : start,
            overlap: false,
            draft: true,
        })
        return list.sort(
            (a, b) => a.sort - b.sort || Number(a.draft) - Number(b.draft)
        )
    }, [events, hits, timeLabel, caseShort, typeMeta, start, end, activityType, otherName])

    /* ------------------------------------------------------------ save -- */

    const save = async () => {
        setError('')
        if (!mediatorId) return setError('Choose a mediator first.')
        if (!activityType) return setError('Choose an activity.')
        if (activityType === 'other' && !otherName.trim()) {
            return setError('Name the activity.')
        }
        if (!date) return setError('A date is required.')

        setIsSaving(true)
        try {
            const payload = {
                /* The day was shown in full before this point, so there is
                 * nothing left to warn about — the 409 path belongs to the old
                 * save-then-surprise flow. Editing never warned anyway. */
                force: true,
                mediator_id: mediatorId,
                close_slot_id:
                    choice === 'Close it off' && conflict?.kind === 'slot'
                        ? conflict.id
                        : null,
                case_id: selectedCase?.id || null,
                activity_type: activityType,
                other_name: activityType === 'other' ? otherName.trim() : null,
                method: method || null,
                date,
                time: timeOpen ? time : '',
                /* An empty duration falls back to 30 — the placeholder said
                   so, and a timed activity has to have a length. */
                duration_minutes: timeOpen && time ? Number(duration) || 30 : null,
                notes: note.trim() || null,
            }
            const res = isEditing
                ? await api.patch(`/activities/${activity.id}`, payload)
                : await api.post('/activities', payload)
            /* No toast here — the mediator's portal confirms with one, and in
               this app the caller closes the dialog and refreshes. The saved
               row is handed back, which is what the existing callers expect,
               with `slotClosed` beside it: closing a slot writes to
               AVAILABILITY, and the board reloads only its activity chips, so
               without this the slot keeps rendering Available. */
            onSaved?.(res?.data?.data, { slotClosed: !!res?.data?.slot_closed })
        } catch (err) {
            setError(
                err?.response?.data?.message || 'The activity could not be saved.'
            )
        } finally {
            setIsSaving(false)
        }
    }

    const scheduleRef = useRef(null)

    const submit = () => {
        if (isSaving) return
        if (!activityType) return setError('Choose an activity.')
        if (activityType === 'other' && !otherName.trim()) {
            return setError('Name the activity.')
        }
        /* "Add a time" was pressed but nothing valid typed: scroll to it
           rather than save something the mediator did not finish. */
        if (timeOpen && !time) {
            scheduleRef.current?.scrollIntoView({ block: 'center' })
            return undefined
        }
        if (saveBlocked) return undefined
        return save()
    }

    const onChoice = (c) => {
        if (c === 'Change time') {
            setChoice(null)
            scheduleRef.current?.scrollIntoView({ block: 'center' })
            return
        }
        setChoice(c)
    }

    /* The footer says why Save is waiting. What a made choice will DO is said
       in the day panel, under the choice itself. */
    const hint = !date
        ? 'Choose a date to save.'
        : timeInvalid
        ? 'That is not a valid time.'
        : timeIncomplete
        ? 'Finish the start time, or remove it.'
        : !dayLoaded
        ? 'Checking your day…'
        : decisionPending
        ? 'Choose an option to save.'
        : ''

    const resolutionTitle = !conflict
        ? ''
        : conflict.kind === 'med'
        ? 'Falls during a booked mediation'
        : conflict.kind === 'act'
        ? 'Overlaps another activity'
        : 'Should this slot stay bookable?'

    /* =========================================================== render == */
    return (
        /* FULL-BLEED, by cancelling `.dialog-content`'s own p-6, so the rule
         * under the header and the tinted footer run to the dialog's edges.
         * The padding is put back per band below. Every size and colour here
         * was read off the design file's computed styles. */
        <div
            style={{
                fontFamily: 'inherit',
                color: T.ink,
                margin: -24,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
            }}
        >
            {/* ---------------------------------------------------- header */}
            <div
                style={{
                    padding: '25px 28px 20px',
                    borderBottom: `1px solid ${T.line}`,
                }}
            >
                {headerLead}
                <h5
                    style={{
                        margin: 0,
                        fontSize: 20,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        letterSpacing: '-0.2px',
                        color: T.ink,
                    }}
                >
                    {isEditing ? 'Edit activity' : 'Add activity'}
                </h5>
                <p style={{ margin: '5px 0 0', fontSize: 13, color: T.muted }}>
                    Add to your calendar and case timeline
                </p>
            </div>

            {/* ------------------------------------------------------ body */}
            <div data-activity-body style={{ padding: '25px 28px 27px' }}>
                {/* --------------------------------------------- details */}
                <div style={{ marginBottom: 22 }}>
                    <div style={label13}>Case {optional}</div>
                    {lockedCase ? (
                        <div
                            style={{
                                height: 44,
                                border: `1px solid ${T.border}`,
                                borderRadius: 7,
                                padding: '0 11px',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 15,
                                background: '#F7F9F8',
                                color: T.mutedSoft,
                            }}
                        >
                            {lockedCase.case_name}
                        </div>
                    ) : (
                        <CaseSelect
                            value={selectedCase}
                            options={caseOptions}
                            query={caseQuery}
                            onQueryChange={setCaseQuery}
                            loading={caseLoading}
                            disabled={isSaving}
                            onChange={setSelectedCase}
                        />
                    )}
                </div>

                <div style={{ marginBottom: 22 }}>
                    <div style={label13}>Activity</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                        {TYPES.map((t) => (
                            <Chip
                                key={t.value}
                                on={activityType === t.value}
                                onClick={() => {
                                    setActivityType(t.value)
                                    setError('')
                                }}
                            >
                                {t.label}
                            </Chip>
                        ))}
                    </div>
                    {activityType === 'other' && (
                        <input
                            value={otherName}
                            onChange={(e) => setOtherName(e.target.value)}
                            placeholder="Name this activity"
                            style={{
                                marginTop: 10,
                                width: '100%',
                                boxSizing: 'border-box',
                                height: 44,
                                border: `1px solid ${T.border}`,
                                borderRadius: 7,
                                padding: '0 11px',
                                font: 'inherit',
                                fontSize: 15,
                                outlineColor: T.green,
                            }}
                        />
                    )}
                </div>

                <div style={{ marginBottom: 22 }}>
                    <div style={label13}>Method {optional}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                        {METHODS.map((m) => (
                            <Chip
                                key={m.value}
                                on={method === m.value}
                                /* Deselectable, unlike Activity: the method is
                                   optional, so the chip has to be able to go
                                   back to none. */
                                onClick={() =>
                                    setMethod(method === m.value ? '' : m.value)
                                }
                            >
                                {m.label}
                            </Chip>
                        ))}
                    </div>
                </div>

                <div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            gap: 10,
                            marginBottom: 9,
                        }}
                    >
                        <label
                            htmlFor="activity-notes"
                            style={{ ...label13, marginBottom: 0 }}
                        >
                            Notes
                        </label>
                        <span
                            style={{
                                fontSize: 12,
                                color: T.muted,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Shows on the case timeline
                        </span>
                    </div>
                    <textarea
                        id="activity-notes"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What would you like to cover?"
                        style={{
                            display: 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            minHeight: 107,
                            border: `1px solid ${T.borderSoft}`,
                            borderRadius: 7,
                            padding: '11px 12px',
                            font: 'inherit',
                            fontSize: 15,
                            lineHeight: 1.45,
                            resize: 'vertical',
                            outlineColor: T.green,
                        }}
                    />
                </div>

                <div style={{ height: 1, background: T.line, margin: '25px 0' }} />

                {/* ----------------------------------------- date and time */}
                <div ref={scheduleRef}>
                    <Scheduling
                        mediatorId={mediatorId}
                        timeInvalid={timeInvalid}
                        date={date}
                        setDate={setDate}
                        timeOpen={timeOpen}
                        setTimeOpen={setTimeOpen}
                        time={time}
                        clockText={clockText}
                        setClockText={setClockText}
                        period={period}
                        setPeriod={setPeriod}
                        duration={duration}
                        setDuration={setDuration}
                        end={end}
                        dateMenu={dateMenu}
                        setDateMenu={setDateMenu}
                    />
                </div>

                {/* ------------------------------------------------ your day */}
                <YourDay
                    date={date}
                    rows={rows}
                    events={events}
                    loaded={dayLoaded}
                    title={resolutionTitle}
                    message={message}
                    choices={choices}
                    choice={choice}
                    onChoice={onChoice}
                />
            </div>

            {error && (
                <div style={{ padding: '0 28px 16px' }}>
                    <p
                        style={{
                            margin: 0,
                            background: '#FEF2F2',
                            color: '#B91C1C',
                            borderRadius: 7,
                            padding: '8px 12px',
                            fontSize: 14,
                        }}
                    >
                        {error}
                    </p>
                </div>
            )}

            {/* ---------------------------------------------------- footer */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 18,
                    padding: '17px 28px',
                    background: T.footer,
                    borderTop: `1px solid ${T.line}`,
                    /* Matches the dialog's `rounded-2xl`, or the tint squares
                       off the two bottom corners the dialog rounds. */
                    borderBottomLeftRadius: 16,
                    borderBottomRightRadius: 16,
                }}
            >
                <button
                    type="button"
                    onClick={() => onCancel?.()}
                    disabled={isSaving}
                    style={{
                        height: 44,
                        padding: '0 18px',
                        borderRadius: 7,
                        border: `1px solid ${T.border}`,
                        background: T.white,
                        font: 'inherit',
                        fontSize: 15,
                        fontWeight: 600,
                        color: T.inkSoft,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Cancel
                </button>

                {/* NO DELETE HERE. Every surface that lists an activity already
                    carries one on the row's "…" menu, and this form is reached
                    FROM one of those menus. Editing edits. */}

                <span
                    role="status"
                    style={{
                        marginLeft: 'auto',
                        fontSize: 13,
                        textAlign: 'right',
                        lineHeight: 1.5,
                        color: T.muted,
                    }}
                >
                    {hint}
                </span>

                <button
                    type="button"
                    onClick={submit}
                    disabled={isSaving || saveBlocked}
                    style={{
                        height: 44,
                        padding: '0 18px',
                        borderRadius: 7,
                        border: 0,
                        color: T.white,
                        font: 'inherit',
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: isSaving ? 'wait' : 'pointer',
                        whiteSpace: 'nowrap',
                        flex: 'none',
                        background: saveBlocked ? T.greenDisabled : T.green,
                    }}
                >
                    {isSaving ? 'Saving…' : 'Save activity'}
                </button>
            </div>
        </div>
    )
}

/* ================================================== date and time ======= */

const Scheduling = ({
    /* Threaded through for the month popover's dots, which ask for that
       mediator's activities. */
    mediatorId,
    /* Computed in the parent, because the footer hint needs it too. */
    timeInvalid,
    date,
    setDate,
    timeOpen,
    setTimeOpen,
    time,
    clockText,
    setClockText,
    period,
    setPeriod,
    duration,
    setDuration,
    end,
    dateMenu,
    setDateMenu,
}) => {

    const fieldBtn = {
        width: '100%',
        height: 44,
        border: `1px solid ${T.border}`,
        borderRadius: 7,
        padding: '0 10px 0 12px',
        font: 'inherit',
        fontSize: 15,
        color: T.ink,
        boxSizing: 'border-box',
        background: T.white,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    }

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 24,
                alignItems: 'start',
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div style={{ ...label13, lineHeight: '20px' }}>Date</div>
                {/* A FIELD that opens our own month, not <input type="date">.
                    The native control renders the browser's own ordering — on
                    a US locale "11/12/2026", which is the format Adam asked us
                    to stop showing. This spells the date out. */}
                <div style={{ position: 'relative' }}>
                    <button
                        type="button"
                        aria-haspopup="dialog"
                        aria-expanded={dateMenu}
                        onClick={() => setDateMenu(!dateMenu)}
                        style={{
                            ...fieldBtn,
                            borderColor: dateMenu ? T.green : T.border,
                        }}
                    >
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {date ? (
                                usLongDate(date)
                            ) : (
                                <span style={{ color: T.faint }}>Choose a date</span>
                            )}
                        </span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#5B6663"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            style={{ flex: 'none', marginLeft: 'auto' }}
                        >
                            <rect x="3" y="5" width="18" height="16" rx="2" />
                            <path d="M3 10h18M8 3v4M16 3v4" />
                        </svg>
                    </button>
                    {dateMenu && (
                        <MonthPicker
                            value={date}
                            mediatorId={mediatorId}
                            onPick={(d) => {
                                setDate(d)
                                setDateMenu(false)
                            }}
                            onClose={() => setDateMenu(false)}
                        />
                    )}
                </div>
            </div>

            {!timeOpen ? (
                <div style={{ minWidth: 0 }}>
                    <div style={{ ...label13, lineHeight: '20px' }}>Time {optional}</div>
                    {/* THE DEFAULT IS NO TIME. Adam: "even though it says
                        optional, I feel like I have to put in a time, and the
                        desired outcome is that they don't put in a time".
                        No explanatory line: "optional" beside the label and a
                        button you have to press already say it, and a sentence
                        under them was the third way of saying the same thing.
                        Sized to its words — full width reads as a field waiting
                        to be filled, which is the opposite of what this state
                        means — but the SAME 44px height as the date field
                        beside it, so the two line up. */}
                    <button
                        type="button"
                        onClick={() => setTimeOpen(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 7,
                            height: 44,
                            boxSizing: 'border-box',
                            padding: '0 16px',
                            border: `1px solid ${T.border}`,
                            borderRadius: 8,
                            background: T.white,
                            font: 'inherit',
                            fontSize: 14,
                            fontWeight: 500,
                            color: T.green,
                            cursor: 'pointer',
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        Add a time
                    </button>
                </div>
            ) : (
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                marginBottom: 9,
                            }}
                        >
                            <span style={{ ...label13, marginBottom: 0, lineHeight: '20px' }}>
                                Start time {optional}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setTimeOpen(false)
                                    setClockText('')
                                }}
                                style={{
                                    border: 0,
                                    background: 'none',
                                    padding: 0,
                                    font: 'inherit',
                                    fontSize: 13,
                                    lineHeight: '20px',
                                    color: T.green,
                                    textDecoration: 'underline',
                                    textUnderlineOffset: 3,
                                    cursor: 'pointer',
                                }}
                            >
                                Remove
                            </button>
                        </div>
                        {/* ONE FIELD FOR THE WHOLE TIME: the clock, AM/PM
                            and the duration all live in a single bordered box
                            — "10:30 PM  30 min". They are one decision about
                            when this happens, and three separate controls made
                            it read as three. */}
                        <div
                            style={{
                                ...fieldBtn,
                                padding: 0,
                                overflow: 'visible',
                                borderColor: timeInvalid ? '#B91C1C' : T.border,
                                cursor: 'text',
                            }}
                        >
                            <input
                                value={clockText}
                                onChange={(e) => {
                                    const normalized = normalizeClockInput(e.target.value, period, clockText)
                                    setClockText(normalized.text)
                                    setPeriod(normalized.period)
                                }}
                                aria-invalid={timeInvalid}
                                aria-describedby="activity-time-hint"
                                placeholder="00:00"
                                inputMode="numeric"
                                aria-label="Start time"
                                size={5}
                                style={{
                                    ...INPUT_IN_FIELD,
                                    flex: '1 1 0%',
                                    minWidth: 0,
                                    padding: '0 10px',
                                }}
                            />
                            <span style={DIVIDER_IN_FIELD} />
                            {['AM', 'PM'].map((pp) => (
                                <button
                                    key={pp}
                                    type="button"
                                    onClick={() => setPeriod(pp)}
                                    aria-pressed={period === pp}
                                    style={{
                                        flex: 'none',
                                        alignSelf: 'stretch',
                                        padding: '0 9px',
                                        border: 0,
                                        background: period === pp ? T.greenWash : 'transparent',
                                        color: period === pp ? T.greenDark : T.muted,
                                        font: 'inherit',
                                        fontSize: 13,
                                        fontWeight: period === pp ? 600 : 400,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {pp}
                                </button>
                            ))}
                            <span style={DIVIDER_IN_FIELD} />
                            <ActivityDurationSelect value={duration} onChange={setDuration} />
                        </div>
                        {/* CAPTIONS, NOT AN ERROR. A half-typed time is not a
                            mistake, it is a time being typed — turning the box
                            red mid-entry scolds the mediator for keystrokes
                            they have not finished. The format sits under the
                            clock and the unit under the duration, each beneath
                            the part it describes. */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 12,
                                margin: '7px 2px 0',
                                fontSize: 12,
                                color: T.faint,
                            }}
                        >
                            {/* An EXAMPLE of the whole field, not the token
                                "hh:mm" — the placeholder already shows the
                                shape, so the caption is better spent showing
                                what a finished entry looks like. */}
                            <span id="activity-time-hint" role={timeInvalid ? 'alert' : undefined} style={timeInvalid ? { color: '#B91C1C' } : undefined}>
                                {timeInvalid
                                    ? 'Use hours 00–23 and minutes 00–59.'
                                    : 'e.g. 10:30 AM or 13:00 → 1:00 PM'}
                            </span>
                            <span>Duration</span>
                        </div>
                        {time && (
                            <p style={{ ...helpText, marginTop: 10 }}>
                                Ends at {fmtMinutes(end)}.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

/* ======================================================== your day ====== */

const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    borderRadius: 999,
    padding: '4px 8px',
    fontSize: 11,
    lineHeight: 1.3,
    fontWeight: 500,
    borderWidth: 1,
    borderStyle: 'solid',
}

const YourDay = ({
    date,
    rows,
    events,
    loaded,
    title,
    message,
    choices,
    choice,
    onChoice,
}) => {
    const cell = {
        padding: '13px 0',
        verticalAlign: 'middle',
        borderBottom: `1px solid ${T.line}`,
    }
    const hasDecision = loaded && choices.length > 0
    const consequence = consequenceOf(choice)

    return (
        <section
            aria-label="Your day"
            style={{
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: 18,
                marginTop: 25,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '6px 15px',
                    marginBottom: events.length || !loaded ? 18 : 10,
                }}
            >
                <span style={{ fontSize: 15, fontWeight: 600 }}>Your day</span>
                <span style={{ fontSize: 13, color: T.muted, whiteSpace: 'nowrap' }}>
                    {date ? longDayLabel(date) : 'No date yet'}
                </span>
            </div>

            {!date ? (
                <p style={{ margin: 0, fontSize: 13, color: T.muted }}>
                    Pick a date to see what else is on it.
                </p>
            ) : !loaded ? (
                /* Dimmed placeholder ROWS, not the word "Loading": the panel
                   keeps its height and does not jump when the day lands. */
                [0, 1, 2].map((i) => (
                    <div
                        key={i}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            height: 40,
                        }}
                    >
                        <span style={{ width: 70, height: 9, borderRadius: 4, background: '#E4E9E7' }} />
                        <span style={{ width: 6, height: 6, borderRadius: 2, background: '#E4E9E7' }} />
                        <span
                            style={{
                                flex: 1,
                                maxWidth: 150 + i * 40,
                                height: 9,
                                borderRadius: 4,
                                background: '#E4E9E7',
                            }}
                        />
                    </div>
                ))
            ) : !events.length ? (
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: T.muted }}>
                    {message || 'Nothing else is scheduled that day.'}
                </p>
            ) : (
                <table
                    aria-label="Schedule for the selected day"
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                    }}
                >
                    <thead>
                        <tr>
                            {[
                                ['Time', 107],
                                ['Activity', undefined],
                                ['', 100],
                            ].map(([h, w], i) => (
                                <th
                                    key={i}
                                    scope="col"
                                    style={{
                                        width: w,
                                        textAlign: 'left',
                                        fontSize: 12,
                                        fontWeight: 400,
                                        color: T.muted,
                                        padding: '0 0 9px',
                                        borderBottom: `1px solid ${T.line}`,
                                    }}
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <td
                                    style={{
                                        ...cell,
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                        color: T.mutedSoft,
                                        fontVariantNumeric: 'tabular-nums',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {r.from ? (
                                        <>
                                            {r.from}
                                            <span
                                                style={{
                                                    display: 'block',
                                                    fontSize: 12,
                                                    color: T.muted,
                                                }}
                                            >
                                                – {r.to}
                                            </span>
                                        </>
                                    ) : (
                                        'No set time'
                                    )}
                                </td>
                                <td style={{ ...cell, paddingRight: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: 2,
                                                marginRight: 8,
                                                flex: 'none',
                                                background: r.color,
                                            }}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                            <span
                                                style={{
                                                    display: 'block',
                                                    fontSize: 14,
                                                    lineHeight: 1.5,
                                                    fontWeight: r.draft ? 600 : 400,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {r.name}
                                            </span>
                                            {r.sub && (
                                                <span
                                                    style={{
                                                        display: 'block',
                                                        marginTop: 3,
                                                        fontSize: 13,
                                                        lineHeight: 1.5,
                                                        color: T.muted,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {r.sub}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td
                                    style={{
                                        ...cell,
                                        textAlign: 'right',
                                        fontSize: 12,
                                        color: T.muted,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {r.draft ? (
                                        <span style={{ ...pill, borderColor: '#8EADA5', color: T.green }}>
                                            {r.status}
                                        </span>
                                    ) : r.overlap ? (
                                        <span style={{ ...pill, borderColor: '#BBA386', color: '#865D30' }}>
                                            {r.status}
                                        </span>
                                    ) : (
                                        r.status
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* A line with no decision attached — "No overlap. You're good to
                save." — sits under the list as a quiet note. */}
            {loaded && events.length > 0 && message && !hasDecision && (
                <p style={{ margin: '14px 0 0', fontSize: 13, lineHeight: 1.65, color: T.muted }}>
                    {message}
                </p>
            )}

            {hasDecision && (
                <div style={{ marginTop: 20, paddingTop: 18 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 5 }}>{title}</div>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: T.muted }}>
                        {message}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 9, marginTop: 13 }}>
                        {choices.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => onChoice(c)}
                                aria-pressed={choice === c}
                                style={{
                                    whiteSpace: 'nowrap',
                                    flex: 'none',
                                    minHeight: 36,
                                    padding: '7px 11px',
                                    borderRadius: 7,
                                    font: 'inherit',
                                    fontSize: 13,
                                    cursor: 'pointer',
                                    background: T.white,
                                    color: T.inkSoft,
                                    border: `1px solid ${choice === c ? T.green : T.border}`,
                                    boxShadow: choice === c ? `inset 0 0 0 1px ${T.green}` : 'none',
                                }}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    {consequence && (
                        <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.5, color: T.muted }}>
                            {consequence}.
                        </p>
                    )}
                </div>
            )}
        </section>
    )
}

/* ======================================================== shared bits === */

/** The popover shell: time, duration and the month all sit in one of these. */
const Menu = ({ children, onClose, maxHeight, width }) => {
    const ref = useRef(null)
    /* A scrolling list stops short of the footer rather than running into it.
     *
     * Measured against the body band, not hard-coded: the cap depends on where
     * in the step the field sits, and a fixed number went stale the moment the
     * body's height changed. Only lists are clamped — the month popover is
     * meant to overhang the footer, as it does in the mockup. */
    const [cap, setCap] = useState(maxHeight)
    const placement = useActivityMenuPlacement(ref, cap)
    useLayoutEffect(() => {
        const box = ref.current
        if (!box || !maxHeight) return
        const body = box.closest('[data-activity-body]')
        if (!body) return
        const room = body.getBoundingClientRect().bottom - box.getBoundingClientRect().top - 12
        setCap(Math.max(144, Math.min(maxHeight, room)))
    }, [maxHeight])
    useEffect(() => {
        const onDown = (e) => {
            if (!ref.current?.contains(e.target)) onClose()
        }
        const onKey = (e) => {
            /* Escape closes the MENU, and must not reach the dialog — otherwise
               picking a time and changing your mind throws the whole form away. */
            if (e.key === 'Escape') {
                e.stopPropagation()
                onClose()
            }
        }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey, true)
        }
    }, [onClose])

    /* Open AT the current value, so 96 quarter hours do not open at midnight.
     *
     * Done by setting this box's own scrollTop, NOT by scrollIntoView on the
     * row: that walks every scrollable ancestor, and the one above this is the
     * dialog overlay — it would have yanked the whole modal up the page to
     * centre a menu row. Once, on mount. */
    useEffect(() => {
        const box = ref.current
        const sel = box?.querySelector('[data-selected="true"]')
        if (!box || !sel) return
        box.scrollTop = sel.offsetTop - box.clientHeight / 2 + sel.offsetHeight / 2
        /* After `cap`, not before: the centring maths reads clientHeight, and
           on the first pass that is still the unclamped height. */
    }, [cap])
    return (
        <div
            ref={ref}
            style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                right: width ? 'auto' : 0,
                width,
                zIndex: 10003,
                background: T.white,
                border: `1px solid ${T.borderMenu}`,
                borderRadius: 10,
                boxShadow: '0 10px 30px rgba(16,32,28,.13)',
                padding: 6,
                maxHeight: cap,
                overflowY: maxHeight ? 'auto' : undefined,
                ...placement,
                boxSizing: 'border-box',
            }}
        >
            {children}
        </div>
    )
}


/* ========================================================= the month ==== */

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/* scale-portal reaches for dayjs here; this app does not carry it, and the two
 * calls it was doing are a format and a start-of-month. Parsed from the PARTS,
 * never `new Date('2026-10-14')` — a bare date string is read as UTC and
 * renders as the previous day for anyone west of Greenwich. */
function parseLocalDate(iso) {
    const [y, m, d] = String(iso || '').split('-').map(Number)
    if (!y || !m || !d) return new Date()
    return new Date(y, m - 1, d)
}

/** The first of the month a date falls in, as a Date. */
function startOfMonth(iso) {
    const d = iso ? parseLocalDate(iso) : new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** A Date -> "2026-10-14", built from the local parts. */
function isoOf(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
    ).padStart(2, '0')}`
}

const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1)
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)

/** "2026-10-14" -> "Wednesday, Oct 14". */
function longDayLabel(iso) {
    return parseLocalDate(iso).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    })
}

/**
 * The date popover.
 *
 * It fetches the displayed month's activities purely to put a dot under the
 * days that already have one — the mockup's quiet "you have something that
 * day" cue, and the cheapest version of the step-3 message a mediator can get
 * before they have even chosen the date. It is decoration: a failed fetch
 * leaves the dots off and the picker works exactly the same.
 */
const MonthPicker = ({ value, mediatorId, onPick, onClose }) => {
    const today = isoOf(new Date())
    const [cursor, setCursor] = useState(() => startOfMonth(value))
    const [busy, setBusy] = useState(() => new Set())

    useEffect(() => {
        let dead = false
        const from = isoOf(cursor)
        const to = isoOf(endOfMonth(cursor))
        api.get('/activities', { params: { mediatorId, from, to } })
            .then((res) => {
                if (dead) return
                const rows = res?.data?.data || []
                setBusy(new Set(rows.map((r) => r.local_date).filter(Boolean)))
            })
            .catch(() => {})
        return () => {
            dead = true
        }
    }, [cursor, mediatorId])

    const cells = []
    /* Six full weeks, always — a month that needs five and a month that needs
       six must not resize the popover under the cursor. */
    const gridStart = addDays(cursor, -cursor.getDay())
    for (let i = 0; i < 42; i += 1) cells.push(addDays(gridStart, i))

    const cellBtn = {
        height: 38,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontSize: 15,
        borderRadius: 10,
        cursor: 'pointer',
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        padding: 0,
    }

    return (
        <Menu onClose={onClose} width={296}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 4px 10px',
                }}
            >
                <Arrow dir="prev" onClick={() => setCursor(addMonths(cursor, -1))} />
                <span style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {cursor.toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                    })}
                </span>
                <Arrow dir="next" onClick={() => setCursor(addMonths(cursor, 1))} />
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    textAlign: 'center',
                }}
            >
                {WEEKDAYS.map((d, i) => (
                    <span
                        key={`${d}${i}`}
                        style={{ fontSize: 12, color: T.faint, paddingBottom: 6 }}
                    >
                        {d}
                    </span>
                ))}
                {cells.map((d) => {
                    const iso = isoOf(d)
                    const outside = d.getMonth() !== cursor.getMonth()
                    const picked = iso === value
                    /* NO PAST DATES. An activity is something you are going to
                       do, so a day that has gone is not a choice — it would be
                       saved, mailed and put on a calendar for a date nobody
                       can attend. The day ALREADY on the activity stays
                       selectable, so editing one booked last week does not
                       force its date to move. */
                    const past = iso < today && iso !== value
                    return (
                        <button
                            key={iso}
                            type="button"
                            disabled={past}
                            aria-disabled={past}
                            title={past ? 'This day has passed' : undefined}
                            onClick={() => !past && onPick(iso)}
                            style={{
                                ...cellBtn,
                                background: picked ? T.green : 'none',
                                color: picked
                                    ? T.white
                                    : past
                                    ? '#D3D9D7'
                                    : outside
                                    ? '#C3CBC8'
                                    : T.ink,
                                fontWeight: picked ? 600 : 400,
                                cursor: past ? 'default' : 'pointer',
                            }}
                        >
                            {d.getDate()}
                            {busy.has(iso) && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        bottom: 5,
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        background: picked ? T.white : T.green,
                                    }}
                                />
                            )}
                        </button>
                    )
                })}
            </div>
        </Menu>
    )
}

const Arrow = ({ dir, onClick }) => (
    <button
        type="button"
        aria-label={dir === 'prev' ? 'Previous month' : 'Next month'}
        onClick={onClick}
        style={{
            width: 30,
            height: 30,
            border: 0,
            background: 'none',
            borderRadius: 8,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            color: T.inkSoft,
            padding: 0,
        }}
    >
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d={dir === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
        </svg>
    </button>
)

export default AddActivityForm
